import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ExternalLink,
  Filter,
  GripVertical,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Ellipsis,
} from "lucide-react";
import {
  ControlWithSessions,
  DryControlSession,
} from "../../../../hooks/useControlsV2";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { cn } from "../../../../lib/utils";
import { TestPhases } from "../../../../models/audit/controlSession";

const MAX_SESSIONS_DEFAULT = 3;

type SessionStatus =
  | "in_progress"
  | "in_review"
  | "complete"
  | null
  | undefined;

function SessionStatusBadge({ status }: { status: SessionStatus }) {
  if (!status) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">
        Not Started
      </span>
    );
  }
  const styles: Record<string, string> = {
    in_progress: "bg-blue-100 text-blue-700",
    in_review: "bg-amber-100 text-amber-700",
    complete: "bg-green-100 text-green-700",
  };
  const labels: Record<string, string> = {
    in_progress: "In Progress",
    in_review: "Pending Review",
    complete: "Complete",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 text-xs rounded-full",
        styles[status],
      )}
    >
      {labels[status]}
    </span>
  );
}

interface ColumnWidths {
  name: number;
  status: number;
  testPhase: number;
  fyCycle: number;
  testName: number;
  creator: number;
  created: number;
}

interface FieldworkTableProps {
  controls: ControlWithSessions[];
  loading: boolean;
  hasActiveFilters: boolean;
  lastRowRef?: (node: HTMLTableRowElement | null) => void;
  onCreateSession: (controlId: string) => void;
  onDeleteSession: (session: DryControlSession) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (field: string) => void;
}

export function FieldworkTable({
  controls,
  loading,
  hasActiveFilters,
  lastRowRef,
  onCreateSession,
  onDeleteSession,
  sortBy,
  sortOrder,
  onSort,
}: FieldworkTableProps) {
  const navigate = useNavigate();

  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({
    name: 300,
    status: 120,
    testPhase: 120,
    fyCycle: 100,
    testName: 200,
    creator: 160,
    created: 140,
  });

  const [resizingColumn, setResizingColumn] = useState<
    keyof ColumnWidths | null
  >(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(100, startWidthRef.current + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [resizingColumn]);

  const handleResizeStart = (
    e: React.MouseEvent,
    column: keyof ColumnWidths,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(column);
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[column];
  };

  const ColumnHeader = ({
    field,
    colKey,
    children,
    sortable = false,
    className,
  }: {
    field?: string;
    colKey: keyof ColumnWidths;
    children: React.ReactNode;
    sortable?: boolean;
    className?: string;
  }) => {
    const isSorted = sortable && field && sortBy === field;
    const Icon = isSorted
      ? sortOrder === "asc"
        ? ArrowUp
        : ArrowDown
      : ArrowUpDown;

    return (
      <th
        onClick={() => sortable && field && onSort?.(field)}
        style={{
          width: columnWidths[colKey],
          minWidth: columnWidths[colKey],
        }}
        className={cn(
          "group relative border-y border-l border-stone-200 bg-stone-50 px-4 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wide select-none",
          sortable && "cursor-pointer hover:bg-stone-100 transition-colors",
          className,
        )}
      >
        <div className="flex items-center gap-1.5 overflow-hidden">
          <span className="truncate">{children}</span>
          {sortable && (
            <Icon
              className={cn(
                "h-3 w-3 shrink-0",
                isSorted ? "opacity-100" : "opacity-40",
              )}
            />
          )}
        </div>
        <div
          className="absolute right-0 top-0 bottom-0 w-4 cursor-col-resize flex items-center justify-center z-10"
          onMouseDown={(e) => handleResizeStart(e, colKey)}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </th>
    );
  };

  // Track which controls are expanded (show all sessions)
  const [expandedControls, setExpandedControls] = useState<Set<string>>(
    new Set(),
  );

  const toggleExpanded = (controlId: string) => {
    setExpandedControls((prev) => {
      const next = new Set(prev);
      if (next.has(controlId)) {
        next.delete(controlId);
      } else {
        next.add(controlId);
      }
      return next;
    });
  };

  const handleSessionClick = (session: DryControlSession) => {
    navigate(`/audit/control-sessions/session/${session.id}`);
  };

  const handleControlClick = (e: React.MouseEvent, controlId: string) => {
    e.stopPropagation();
    navigate(`/audit/controls/v2/${controlId}`);
  };

  if (loading && controls.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loading && controls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-stone-500">
        <Filter className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium mb-2">No controls found</p>
        <p className="text-sm">
          {hasActiveFilters
            ? "Try adjusting your filters or search terms"
            : "No controls have been created yet"}
        </p>
      </div>
    );
  }

  return (
    <table
      className="w-full text-sm border-separate border-spacing-0"
      style={{ tableLayout: "fixed" }}
    >
      <thead>
        <tr className="sticky top-0 z-10 bg-white">
          <ColumnHeader field="name" colKey="name" sortable>
            Control
          </ColumnHeader>
          <ColumnHeader colKey="status">Status</ColumnHeader>
          <ColumnHeader colKey="testPhase">Test Phase</ColumnHeader>
          <ColumnHeader colKey="fyCycle">FY</ColumnHeader>
          <ColumnHeader colKey="testName">Test Name</ColumnHeader>
          <ColumnHeader colKey="creator">Test Creator</ColumnHeader>
          <th
            style={{
              width: columnWidths.created,
              minWidth: columnWidths.created,
            }}
            className="group relative border-y border-l border-stone-200 bg-stone-50 px-4 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wide select-none"
          >
            <span className="truncate">Created</span>
            <div
              className="absolute right-0 top-0 bottom-0 w-4 cursor-col-resize flex items-center justify-center z-10"
              onMouseDown={(e) => handleResizeStart(e, "created")}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </th>
          {/* Actions column — fixed width, not resizable */}
          <th className="border-y border-x border-stone-200 bg-stone-50 w-[72px]" />
        </tr>
      </thead>
      <tbody>
        {controls.map((control, controlIndex) => {
          const isLast = controlIndex === controls.length - 1;
          const isExpanded = expandedControls.has(control.id);
          const allSessions = control.controlSessions;
          const displayedSessions = isExpanded
            ? allSessions
            : allSessions.slice(0, MAX_SESSIONS_DEFAULT);
          const hiddenCount = allSessions.length - displayedSessions.length;
          // Whether an expand/collapse row is rendered after the sessions
          const hasExpandRow =
            hiddenCount > 0 ||
            (isExpanded && allSessions.length > MAX_SESSIONS_DEFAULT);

          if (allSessions.length === 0) {
            // Control with no sessions
            return (
              <tr key={control.id} ref={isLast ? lastRowRef : undefined}>
                <td
                  style={{
                    width: columnWidths.name,
                    maxWidth: columnWidths.name,
                  }}
                  className={cn(
                    "px-4 py-3 border-t border-l border-stone-200 align-top overflow-hidden",
                    isLast && "border-b",
                  )}
                >
                  <button
                    onClick={(e) => handleControlClick(e, control.id)}
                    className="text-sm font-medium text-stone-800 hover:underline text-left w-full truncate block"
                    title={control.name}
                  >
                    {control.name}
                  </button>
                </td>
                <td
                  colSpan={5}
                  className={cn(
                    "px-4 py-3 border-t border-l border-stone-200",
                    isLast && "border-b",
                  )}
                >
                  <button
                    onClick={() => onCreateSession(control.id)}
                    className="flex items-center gap-1.5 text-stone-400 hover:text-blue-600 transition-colors text-sm"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Get started
                  </button>
                </td>
                <td
                  className={cn(
                    "border-t border-x border-stone-200",
                    isLast && "border-b",
                  )}
                />
              </tr>
            );
          }

          return (
            <Fragment key={control.id}>
              {displayedSessions.map((session, sessionIndex) => {
                const isFirstSession = sessionIndex === 0;
                const isLastSessionInGroup =
                  sessionIndex === displayedSessions.length - 1;
                // The very last rendered row — used for infinite scroll
                const isLastRow =
                  isLast && isLastSessionInGroup && !hasExpandRow;

                // Top border: solid for first session in group, dashed for subsequent
                const topBorder = isFirstSession
                  ? "border-t border-stone-200"
                  : "border-t [border-top-style:dashed] border-stone-200";

                // Bottom border only on the absolute last row of the table
                const bottomBorder = isLastRow
                  ? "border-b border-stone-200"
                  : "";

                return (
                  <tr
                    key={session.id}
                    ref={isLastRow ? lastRowRef : undefined}
                    className="group cursor-pointer transition-colors hover:bg-stone-50"
                    onClick={() => handleSessionClick(session)}
                  >
                    {/* Control column — name on first row, connector line on rest */}
                    <td
                      style={{
                        width: columnWidths.name,
                        maxWidth: columnWidths.name,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200 align-middle overflow-hidden",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      {isFirstSession ? (
                        <button
                          onClick={(e) => handleControlClick(e, control.id)}
                          className="text-sm font-medium text-stone-800 hover:underline text-left w-full truncate block"
                          title={control.name}
                        >
                          {control.name}
                        </button>
                      ) : (
                        <div className="flex items-center h-full">
                          <div className="w-0.5 h-5 bg-stone-200 ml-0" />
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td
                      style={{
                        width: columnWidths.status,
                        maxWidth: columnWidths.status,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      <SessionStatusBadge status={session.status} />
                    </td>

                    {/* Test Phase */}
                    <td
                      style={{
                        width: columnWidths.testPhase,
                        maxWidth: columnWidths.testPhase,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      <span className="truncate block">
                        <span className="inline-flex items-center px-2.5 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">
                          {TestPhases.find((p) => p.value === session.testPhase)
                            ?.label || "—"}
                        </span>
                      </span>
                    </td>

                    {/* FY Cycle */}
                    <td
                      style={{
                        width: columnWidths.fyCycle,
                        maxWidth: columnWidths.fyCycle,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200 text-sm text-stone-500 overflow-hidden",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      <span className="truncate block">
                        {session.fyCycle?.name ?? "—"}
                      </span>
                    </td>

                    {/* Test / session name */}
                    <td
                      style={{
                        width: columnWidths.testName,
                        maxWidth: columnWidths.testName,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200 overflow-hidden",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm text-stone-700 truncate"
                          title={session.name}
                        >
                          {session.name}
                        </span>
                        <ExternalLink className="h-3.5 w-3.5 text-stone-300 opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
                      </div>
                    </td>

                    {/* Test Creator */}
                    <td
                      style={{
                        width: columnWidths.creator,
                        maxWidth: columnWidths.creator,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200 text-sm text-stone-500 overflow-hidden",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      <span className="truncate block">
                        {session.user?.email ?? "—"}
                      </span>
                    </td>

                    {/* Created */}
                    <td
                      style={{
                        width: columnWidths.created,
                        maxWidth: columnWidths.created,
                      }}
                      className={cn(
                        "px-4 py-3 border-l border-stone-200 text-sm text-stone-400",
                        topBorder,
                        bottomBorder,
                      )}
                    >
                      {new Date(session.createdAt).toLocaleDateString(
                        undefined,
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </td>

                    {/* Actions */}
                    <td
                      className={cn(
                        "px-2 py-3 border-x border-stone-200",
                        topBorder,
                        bottomBorder,
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded text-stone-700 hover:bg-stone-100 transition-colors">
                              <Ellipsis className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isFirstSession && (
                              <DropdownMenuItem
                                onClick={() => onCreateSession(control.id)}
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Add test
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => onDeleteSession(session)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete test
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Expand / collapse row */}
              {hasExpandRow && (
                <tr
                  key={`${control.id}-expand`}
                  ref={isLast ? lastRowRef : undefined}
                >
                  <td
                    className={cn(
                      "px-4 py-2 border-t [border-top-style:dashed] border-l border-stone-200 align-middle",
                      isLast && "border-b",
                    )}
                  >
                    <div className="flex items-center h-full">
                      <div className="w-0.5 h-4 bg-stone-200 ml-0" />
                    </div>
                  </td>
                  <td
                    colSpan={5}
                    className={cn(
                      "px-4 py-2 border-t [border-top-style:dashed] border-l border-stone-200",
                      isLast && "border-b",
                    )}
                  >
                    <button
                      onClick={() => toggleExpanded(control.id)}
                      className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="h-3.5 w-3.5" />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3.5 w-3.5" />
                          {hiddenCount} more session
                          {hiddenCount !== 1 ? "s" : ""}
                        </>
                      )}
                    </button>
                  </td>
                  <td
                    className={cn(
                      "border-t [border-top-style:dashed] border-x border-stone-200",
                      isLast && "border-b",
                    )}
                  />
                </tr>
              )}
            </Fragment>
          );
        })}

        {loading && (
          <tr>
            <td
              colSpan={7}
              className="py-4 border-t border-x border-b border-stone-200"
            >
              <div className="flex items-center justify-center">
                <LoadingSpinner />
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
